import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { auth, db, googleProvider } from '../firebase/config'
import {
  signInWithPopup,
  signInAnonymously,
  sendSignInLinkToEmail,
  signOut
} from 'firebase/auth'
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore'
import { rememberAcquisitionStore } from '../membership/utils/acquisitionStore'
import { getDeviceId } from '../utils/deviceId'

const Intro = ({ store }) => {
  const navigate = useNavigate()

  // Arriving via a per-store poster URL: remember the store so the
  // dashboard survey write can attach it (WEB-Q11 Design B)
  useEffect(() => {
    if (store) rememberAcquisitionStore(store)
  }, [store])

  // Email magic-link entry (WEB-Q9 relaunch): 'collapsed' shows just the
  // pink pill; 'input' expands the email field in place; 'sent' swaps the
  // section for a check-your-inbox card. The link lands on /auth (the
  // completion handler lives there) and continues to the dashboard.
  const [emailEntry, setEmailEntry] = useState('collapsed')
  const [linkEmail, setLinkEmail] = useState('')
  const [sendingLink, setSendingLink] = useState(false)
  const [linkError, setLinkError] = useState('')

  const handleSendLink = async (e) => {
    e.preventDefault()
    if (!linkEmail) return
    setSendingLink(true)
    setLinkError('')
    try {
      if (process.env.REACT_APP_ENVIRONMENT === 'emulator') {
        await sendSignInLinkToEmail(auth, linkEmail, {
          url: `${window.location.origin}/auth?src=web`,
          handleCodeInApp: true
        })
      } else {
        // Same payload as Auth.js: src=web makes the emailed link complete
        // on web immediately in ANY browser (Gmail in-app included), and
        // deviceId feeds the per-device rate-limit layer - this sender had
        // been left behind when Auth.js gained both (fresh-eye 2026-09-03).
        const { getFunctions, httpsCallable } = await import('firebase/functions')
        await httpsCallable(getFunctions(), 'sendMagicLink')({
          email: linkEmail, source: 'web', deviceId: getDeviceId()
        })
      }
      try { window.localStorage.setItem('emailForSignIn', linkEmail) } catch (_) { /* storage-blocked browsers: link still arrives */ }
      setEmailEntry('sent')
    } catch (error) {
      // Callable errors carry our own user-facing message (rate limits
      // especially) - showing "check your connection" for a 429 told the
      // throttled user to retry harder (same bug class Auth.js fixed).
      const code = error.code || ''
      const isFunctions = typeof code === 'string' && code.startsWith('functions/')
      setLinkError(code === 'auth/invalid-email' || /invalid-argument/i.test(code)
        ? 'That email address does not look right - please check it.'
        : (isFunctions && code !== 'functions/internal' && error.message)
          ? error.message
          : 'Could not send the link. Please check your connection and try again.')
    } finally {
      setSendingLink(false)
    }
  }

  const handleGoogleAuth = async () => {
    try {
      console.log('🔐 Starting Google authentication...');

      console.log('🔐 Opening Google popup...');
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;
      console.log('🔐 Google auth successful:', user.email);

      try {
        // Check if user exists in Firestore
        console.log('📊 Checking if user exists in Firestore...');
        const userDoc = await getDoc(doc(db, 'users', user.uid));

        if (!userDoc.exists()) {
          console.log('📊 Creating new user profile...');

          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            name: user.displayName || '',
            points: 0,
            coins: 0,
            stamps: 0,
            emailVerified: true,
            lastSignInTime: serverTimestamp(),
            createdAt: serverTimestamp()
          });
          console.log('✅ User profile created successfully');
        } else {
          console.log('📊 Updating existing user...');
          // Update existing user's emailVerified status and sign-in time
          await setDoc(doc(db, 'users', user.uid), {
            emailVerified: true,
            lastSignInTime: serverTimestamp()
          }, { merge: true });
          console.log('✅ User profile updated');
        }

        console.log('🚀 Navigating to dashboard...');
        navigate('/dashboard');
      } catch (firestoreError) {
        console.error('📊 Firestore error:', firestoreError);
        // If Firestore fails, still allow login but show a warning
        console.warn('Authentication successful, but there was an issue with user data. Some features may not work properly.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('🔐 Google auth error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        console.warn('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        console.warn('Popup was blocked by your browser. Please allow popups and try again.');
      } else {
        console.error('Authentication error:', error.message);
      }
    }
  }

  const handleAnonymousAuthClick = () => {
    handleAnonymousAuth();
  };

  const handleAnonymousAuth = async () => {
    try {
      console.log('🔐 Starting anonymous authentication...');
      // signInAnonymously REUSES the device's persisted anonymous account.
      // The old code then overwrote the existing profile with zeros - wiping
      // returning guests' stamps/points (bug found in the 2026-08-27 audit:
      // at least 24 guests zeroed, active through 2026-08-16). Now:
      // - an existing session younger than 24h reuses its profile untouched
      // - an older session is signed out first, so a FRESH account (new uid,
      //   new doc) is created and the old profile keeps its balances for the
      //   transfer/cleanup flows
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        const existingDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (existingDoc.exists()) {
          const createdAt = existingDoc.data().createdAt;
          const ageMs = createdAt ? Date.now() - createdAt.toDate().getTime() : Infinity;
          if (ageMs < 24 * 60 * 60 * 1000) {
            console.log('🔐 Reusing active anonymous session', auth.currentUser.uid);
            await setDoc(doc(db, 'users', auth.currentUser.uid),
              { lastSignInTime: serverTimestamp() }, { merge: true });
            navigate('/dashboard');
            return;
          }
          // Doc exists but session is older than 24h: sign out so a fresh
          // account is minted; the old profile keeps its balances.
          console.log('🔐 Anonymous session expired - starting fresh');
          await signOut(auth);
        }
        // No doc: fall through - signInAnonymously reuses this same uid and
        // the create path below heals the orphan instead of minting another.
      }
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;
      console.log('🔐 Anonymous auth successful:', user.uid);

      // Create the user profile BEFORE showing the QR. Retry transient
      // failures; on persistent failure remove the just-created anonymous
      // Auth account so it can never be orphaned without a users doc.
      const profile = {
        email: null,
        name: 'Temporary User',
        points: 0,
        coins: 0,
        stamps: 0,
        emailVerified: false,
        isAnonymous: true,
        lastSignInTime: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      let profileCreated = false;
      for (let attempt = 1; attempt <= 3 && !profileCreated; attempt++) {
        try {
          console.log(`📊 Creating anonymous user profile (attempt ${attempt})...`);
          await setDoc(doc(db, 'users', user.uid), profile);
          profileCreated = true;
        } catch (firestoreError) {
          console.error(`📊 Firestore error (attempt ${attempt}):`, firestoreError);
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 500 * attempt));
          }
        }
      }

      if (profileCreated) {
        console.log('✅ Anonymous user profile created, navigating to dashboard');
        navigate('/dashboard');
      } else {
        // Clean up the orphan Auth account (fresh anonymous accounts can
        // delete themselves without re-auth); fall back to sign-out.
        try {
          await user.delete();
        } catch (deleteError) {
          console.error('Could not delete orphan anonymous account:', deleteError);
          try { await auth.signOut(); } catch (_) { /* best effort */ }
        }
        alert('Something went wrong creating your session. Please check your connection and tap "Get your One Time QR Code!" again.');
      }
    } catch (error) {
      console.error('🔐 Anonymous auth error:', error);
      console.error('Authentication error:', error.message);
    }
  }

  const styles = {
    container: {
      fontFamily: 'Raleway, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F8FF 50%, #FFF5F8 100%)',
      color: '#333333',
      minHeight: '100vh',
      margin: 0,
      padding: 0
    },
    header: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(8px)',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      borderBottom: '1px solid rgba(255, 158, 205, 0.2)',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '24px 48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    logo: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#333333',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    logoImage: {
      width: '40px',
      height: '40px',
      objectFit: 'contain'
    },
    heroSection: {
      padding: '80px 24px 40px 24px',
      textAlign: 'center',
      background: 'linear-gradient(135deg, rgba(255, 230, 243, 0.3) 0%, rgba(240, 248, 255, 0.3) 100%)'
    },
    heroContent: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    heroTitle: {
      fontSize: 'clamp(48px, 6vw, 72px)',
      fontWeight: '700',
      color: '#333333',
      lineHeight: '1.1',
      marginBottom: '32px',
      fontFamily: 'Raleway, sans-serif'
    },
    videoSection: {
      maxWidth: '800px',
      margin: '0 auto 32px auto',
      textAlign: 'center'
    },
    videoTitle: {
      fontSize: 'clamp(28px, 4vw, 36px)',
      fontWeight: '600',
      color: '#333333',
      marginBottom: '32px',
      fontFamily: 'Raleway, sans-serif'
    },
    videoWrapper: {
      position: 'relative',
      width: '100%',
      maxWidth: '720px',
      margin: '0 auto 32px auto',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      border: '2px solid rgba(255, 158, 205, 0.3)'
    },
    videoFrame: {
      width: '100%',
      height: '405px',
      border: 'none',
      borderRadius: '20px'
    },
    videoDescription: {
      fontSize: '18px',
      color: '#666666',
      lineHeight: '1.6',
      maxWidth: '600px',
      margin: '0 auto'
    },
    subtitle: {
      fontSize: '20px',
      color: '#666666',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    ctaSection: {
      padding: '40px 24px 80px 24px',
      background: 'linear-gradient(135deg, rgba(255, 158, 205, 0.1) 0%, rgba(240, 248, 255, 0.1) 100%)'
    },
    ctaContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      textAlign: 'center'
    },
    ctaTitle: {
      fontSize: 'clamp(32px, 4vw, 48px)',
      fontWeight: '700',
      color: '#333333',
      marginBottom: '64px',
      fontFamily: 'Raleway, sans-serif'
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      maxWidth: '800px',
      margin: '0 auto'
    },
    button: {
      backgroundColor: '#ff9ecd',
      color: '#ffffff',
      padding: '20px 40px',
      borderRadius: '52px',
      fontSize: '18px',
      fontWeight: '600',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 25px rgba(255, 158, 205, 0.3)',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'Raleway, sans-serif'
    },
    buttonIcon: {
      width: '32px',
      height: '32px',
      fill: 'currentColor'
    },
    tempUserNote: {
      marginTop: '48px',
      padding: '24px',
      backgroundColor: 'rgba(255, 158, 205, 0.1)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 158, 205, 0.2)',
      maxWidth: '600px',
      margin: '48px auto 0 auto'
    },
    tempUserText: {
      fontSize: '16px',
      color: '#666666',
      lineHeight: '1.6',
      margin: '0',
      textAlign: 'center'
    },
    footer: {
      padding: '48px 24px',
      borderTop: '1px solid rgba(255, 158, 205, 0.2)',
      backgroundColor: '#f8f9fa'
    },
    footerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      textAlign: 'center'
    },
    footerText: {
      color: '#666666',
      fontSize: '14px'
    }
  }

  // Media queries for responsive design
  const mediaQueries = `
    @media (min-width: 768px) {
      .hero-title { font-size: 72px !important; }
      .cta-title { font-size: 48px !important; }
      .button-container { flex-direction: row !important; }
      .logo { font-size: 32px !important; }
      .video-frame { height: 405px !important; }
    }
    
    @media (min-width: 1024px) {
      .video-frame { height: 450px !important; }
    }
    
    @media (max-width: 767px) {
      .video-frame { height: 225px !important; }
      .header-content { padding: 16px 24px !important; }
      .cta-section { padding: 64px 24px !important; }
    }
  `

  return (
    <>
      <Helmet>
        <title>Lucky Claww - Your Ultimate Claw Adventure Starts Here!</title>
        <meta name="description" content="Experience the future of claw machines with Lucky Claww's digital gameplay. Download our app or play online for bigger wins and better prizes!" />
        
        {/* Additional SEO and Social Meta Tags */}
        <meta property="og:title" content="Lucky Claww - Your Ultimate Claw Adventure Starts Here!" />
        <meta property="og:description" content="Experience the future of claw machines with Lucky Claww's digital gameplay. Download our app or play online for bigger wins and better prizes!" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lucky Claww - Your Ultimate Claw Adventure Starts Here!" />
        <meta name="twitter:description" content="Experience the future of claw machines with Lucky Claww's digital gameplay. Download our app or play online for bigger wins and better prizes!" />
        
        {/* Google Fonts - Raleway */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link href="https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
        
        <style>{mediaQueries}</style>
        <style>{`
          .temp-user-link {
            color: #ff9ecd !important;
            text-decoration: none !important;
            font-weight: 600 !important;
            transition: all 0.2s ease !important;
            border-bottom: 1px solid transparent !important;
          }
          
          .temp-user-link:hover {
            color: #ff7eb8 !important;
            border-bottom-color: #ff7eb8 !important;
          }
          
          .temp-user-link:focus {
            outline: 2px solid #ff9ecd !important;
            outline-offset: 2px !important;
            border-radius: 4px !important;
          }
          
          .button:hover {
            transform: scale(1.02) !important;
            box-shadow: 0 12px 30px rgba(255, 158, 205, 0.4) !important;
          }
          
          /* Override global button styles for Google sign-in button */
          .google-signin-btn {
            background-color: #4285F4 !important;
            color: #ffffff !important;
            border: 2px solid #4285F4 !important;
            background: #4285F4 !important;
          }
          
          .google-signin-btn:hover {
            background-color: #3367D6 !important;
            background: #3367D6 !important;
            border-color: #3367D6 !important;
          }
          
          /* Force override any inherited button styles */
          button.google-signin-btn {
            background: #4285F4 !important;
            background-color: #4285F4 !important;
          }
          
          button.google-signin-btn:hover {
            background: #3367D6 !important;
            background-color: #3367D6 !important;
          }
        `}</style>
      </Helmet>
      
      <div style={styles.container}>
        {/* Header Section */}
        <header style={styles.header}>
          <div style={styles.headerContent} className="header-content">
            <h1 style={styles.logo} className="logo">
              <img src="/lc-logo.png" alt="Lucky Claww Logo" style={styles.logoImage} />
              Lucky Claww
            </h1>
          </div>
        </header>
        
        {/* Main Content */}
        <main>
          {/* Call-to-Action Section */}
          <section style={styles.ctaSection}>
            <div style={styles.ctaContent}>
              {/* Section Heading */}
              <h2 style={styles.ctaTitle} className="cta-title">
                Get Started Now!
              </h2>
              
              {/* Temporary User Note */}
              <div style={{...styles.tempUserNote, margin: '0 auto 48px auto'}}>
                <p style={styles.tempUserText}>
                  💡 <strong>Don't want to sign up?</strong>
                </p>
                <button 
                  onClick={handleAnonymousAuthClick}
                  style={{
                    ...styles.button,
                    backgroundColor: '#ff9ecd',
                    marginTop: '16px',
                    fontSize: '16px',
                    padding: '16px 32px',
                    border: '2px solid #ff9ecd',
                    display: 'block',
                    margin: '16px auto 0 auto'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#ff7eb8'
                    e.target.style.borderColor = '#ff7eb8'
                    e.target.style.transform = 'scale(1.02)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ff9ecd'
                    e.target.style.borderColor = '#ff9ecd'
                    e.target.style.transform = 'scale(1)'
                  }}
                >
                  <span>Get your One Time QR Code!</span>
                </button>
              </div>
              
              {/* Platform Buttons */}
              <div style={styles.buttonContainer} className="button-container">
                
                
                {/* Google Sign In Button */}
                <button 
                  onClick={handleGoogleAuth}
                  className="google-signin-btn"
                  style={{
                    backgroundColor: '#4285F4',
                    color: '#ffffff',
                    border: '2px solid #4285F4',
                    padding: '20px 40px',
                    borderRadius: '52px',
                    fontSize: '18px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 8px 25px rgba(66, 133, 244, 0.3)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Raleway, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#3367D6'
                    e.target.style.borderColor = '#3367D6'
                    e.target.style.transform = 'scale(1.02)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#4285F4'
                    e.target.style.borderColor = '#4285F4'
                    e.target.style.transform = 'scale(1)'
                  }}
                >
                  {/* Google Logo SVG */}
                  <svg style={styles.buttonIcon} viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#ffffff"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#ffffff"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#ffffff"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#ffffff"/>
                  </svg>
                  <span>Sign in with Google</span>
                </button>

                {emailEntry === 'collapsed' && (
                  <button
                    onClick={() => setEmailEntry('input')}
                    style={{
                      backgroundColor: '#ff69b4',
                      color: '#ffffff',
                      padding: '20px 40px',
                      borderRadius: '52px',
                      fontSize: '18px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '16px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(255, 105, 180, 0.3)',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'Raleway, sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#ff4da6'
                      e.target.style.transform = 'scale(1.02)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#ff69b4'
                      e.target.style.transform = 'scale(1)'
                    }}
                  >
                    <span>✉️ Sign in with Email</span>
                  </button>
                )}

                {emailEntry === 'input' && (
                  <form onSubmit={handleSendLink} style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={linkEmail}
                      onChange={(e) => setLinkEmail(e.target.value)}
                      autoFocus
                      required
                      style={{
                        padding: '20px 32px',
                        borderRadius: '52px',
                        fontSize: '18px',
                        border: '2px solid #ffd1e8',
                        outline: 'none',
                        fontFamily: 'Raleway, sans-serif',
                        textAlign: 'center'
                      }}
                    />
                    {linkError && (
                      <p style={{ color: '#d63384', fontSize: '14px', margin: 0, fontFamily: 'Raleway, sans-serif', textAlign: 'center' }}>
                        {linkError}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={sendingLink}
                      style={{
                        backgroundColor: '#ff69b4',
                        color: '#ffffff',
                        padding: '20px 40px',
                        borderRadius: '52px',
                        fontSize: '18px',
                        fontWeight: '600',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'Raleway, sans-serif',
                        boxShadow: '0 8px 25px rgba(255, 105, 180, 0.3)',
                        opacity: sendingLink ? 0.6 : 1
                      }}
                    >
                      {sendingLink ? 'Sending…' : 'Send sign-in link'}
                    </button>
                  </form>
                )}

                {emailEntry === 'sent' && (
                  <div style={{...styles.tempUserNote, margin: '0 auto'}}>
                    <p style={styles.tempUserText}>
                      📬 <strong>Check your inbox!</strong>
                    </p>
                    <p style={{ ...styles.tempUserText, fontWeight: 'normal', marginTop: '8px' }}>
                      We sent a sign-in link to {linkEmail}. If you don't see it,
                      please check your spam or junk folder.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                      <button
                        onClick={handleSendLink}
                        disabled={sendingLink}
                        style={{ ...styles.button, backgroundColor: '#ff9ecd', border: '2px solid #ff9ecd', padding: '12px 24px', fontSize: '15px' }}
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => { setEmailEntry('input'); setLinkError('') }}
                        style={{ ...styles.button, backgroundColor: '#ff9ecd', border: '2px solid #ff9ecd', padding: '12px 24px', fontSize: '15px' }}
                      >
                        Change email
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </section>
          
          {/* How to Play Section */}
          <section style={styles.heroSection}>
            <div style={styles.heroContent}>
              {/* Main Heading */}
              <h2 style={styles.heroTitle} className="hero-title">
                How to Play
              </h2>
              
              {/* YouTube Video Section */}
              <div style={styles.videoSection}>
                <div style={styles.videoWrapper}>
                  <iframe
                    src="https://www.youtube.com/embed/86J5RoNxnPE"
                    title="How to Play at Lucky Claww - Claw Machine Tutorial"
                    style={styles.videoFrame}
                    className="video-frame"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>

              </div>
            </div>
          </section>

        </main>
        
        {/* Footer Section */}
        <footer style={styles.footer}>
          <div style={styles.footerContent}>
            <p style={styles.footerText}>
              © 2026 Lucky Claww. All Rights Reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}

export default Intro